import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// screen
import { DayTitleUppercase, type LayoutProps } from 'modules/map/components/daily-dish/types';
import { getFontFamily, getIconComponent } from 'modules/map/components/daily-dish/templates/utils';
import { WeekdayTitlePosition } from 'modules/map/components/daily-dish/constants';

/*
 * Layout 3: Two Rows
 */
export const TwoRowsLayout: FC<LayoutProps> = (props) => {
  const { days, settings, maxCount, getDishes } = props;
  const intl = useIntl();

  const isFiveItems = days.length === 5;

  // Split items into rows for 5-item layout: [2 items, 3 items]
  const firstRowItems = isFiveItems ? days.slice(0, 2) : days.slice(0, 3);
  const secondRowItems = isFiveItems ? days.slice(2, 5) : days.slice(3, 6);
  const shouldShowEmoji = settings.menu.emojiEnabled;
  const borderRadius = settings.menu.borderRadius;
  const isTopPosition = settings.menu.weekdayTitlePosition === WeekdayTitlePosition.TOP;

  const renderRow = (weekdays: typeof days) => (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: 'center',
        alignItems: 'stretch',
        width: '60%',
        gap: 2,
        mb: 2,
      }}
    >
      {weekdays.map(({ weekDay, dishes, afternoons }) => {
        const lunchDishes = getDishes(dishes, maxCount.lunch);
        const afternoonDishes = getDishes(afternoons, maxCount.afternoon) as (string[] | null)[];
        const cardColor = settings.menu.weekday[weekDay].borderColor;
        const cardWidth = '360px';
        const cardHeight = '350px';

        const weekdayTitleBox = (
          <Box
            sx={{
              borderTopLeftRadius: isTopPosition ? `${borderRadius}px` : 0,
              borderTopRightRadius: isTopPosition ? `${borderRadius}px` : 0,
              borderBottomLeftRadius: !isTopPosition ? `${borderRadius}px` : 0,
              borderBottomRightRadius: !isTopPosition ? `${borderRadius}px` : 0,
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                position: 'relative',
                textAlign: 'center',
                width: '100%',
                height: '100%',
                fontSize: `${settings.font.weekdayTitle.fontSize}px`,
                color: settings.font.weekdayTitle.fontColor,
                fontFamily: getFontFamily(settings.font.weekdayTitle.fontFamilyId),
                pt: isTopPosition ? 1 : 2,
                pb: isTopPosition ? 2 : 1,
              }}
            >
              {DayTitleUppercase(intl)[weekDay]}
              {shouldShowEmoji &&
                (() => {
                  const IconComponent = getIconComponent(settings.menu.weekday[weekDay].emojiId);

                  return IconComponent ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: isTopPosition ? '4px' : '10px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: '4px',
                      }}
                    >
                      <IconComponent sx={{ color: cardColor, fontSize: '24px' }} />
                    </Box>
                  ) : null;
                })()}
            </Typography>
          </Box>
        );

        const contentBox = (
          <Box
            sx={{
              backgroundColor: '#fff',
              minHeight: '220px',
              p: 1,
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              borderTopLeftRadius: !isTopPosition ? `${borderRadius}px` : 0,
              borderTopRightRadius: !isTopPosition ? `${borderRadius}px` : 0,
              borderBottomLeftRadius: isTopPosition ? `${borderRadius}px` : 0,
              borderBottomRightRadius: isTopPosition ? `${borderRadius}px` : 0,
              lineHeight: 1.4,
            }}
          >
            {lunchDishes.map((dish, idx) => (
              <Typography
                variant="inherit"
                key={idx}
                sx={{
                  userSelect: 'none',
                  color: settings.font.content.fontColor,
                  textAlign: 'center',
                  fontSize: `${settings.font.content.fontSize}px`,
                  fontFamily: getFontFamily(settings.font.content.fontFamilyId),
                }}
              >
                {dish ? dish : <>&nbsp;</>}
              </Typography>
            ))}

            {afternoons.length > 0 && (
              <Divider
                sx={{
                  borderBottom: 1,
                  borderColor: cardColor,
                  width: '100%',
                }}
              />
            )}

            {afternoonDishes.map((meal, idx) => {
              if (!meal) return <>&nbsp;</>;

              const dishName = (meal[0] || '').replace(/\d\./, '');
              const manufacturer = meal[1] || '';

              return (
                <Typography
                  key={idx}
                  sx={{
                    userSelect: 'none',
                    color: settings.font.content.fontColor,
                    textAlign: 'center',
                    fontSize: `${settings.font.content.fontSize}px`,
                    fontFamily: getFontFamily(settings.font.content.fontFamilyId),
                  }}
                  variant="inherit"
                >
                  {`${dishName} ${manufacturer}`}
                </Typography>
              );
            })}
          </Box>
        );

        return (
          <Grid key={weekDay} item xs="auto" sx={{ display: 'flex' }}>
            <Box
              sx={{
                border: `${settings.menu.borderWidth}px solid ${cardColor}`,
                width: cardWidth,
                height: cardHeight,
                borderRadius: `${borderRadius}px`,
                backgroundColor: cardColor,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                lineHeight: 1.4,
              }}
            >
              {isTopPosition ? weekdayTitleBox : contentBox}
              {isTopPosition ? contentBox : weekdayTitleBox}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        rowGap: 2,
        gap: 2,
      }}
    >
      {/* First row: 2 items with space-around (for 5 items) or 3 items with space-between (for 6 items) */}
      {renderRow(firstRowItems)}

      {/* Second row: 3 items with space-between */}
      {renderRow(secondRowItems)}
    </Box>
  );
};

export default TwoRowsLayout;
